import React, { useState, useEffect } from 'react';
import { FaCoins, FaSearch, FaEdit, FaTrash, FaPlus, FaFilter, FaCopy } from 'react-icons/fa';
import { AdminAffiliateApiService } from '../../../service/admin/adminAffiliateApiService';
import './CommissionPlans.css';

export default function CommissionPlans() {
  const [plans, setPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    commissionRate: '',
    bonusStructure: '',
    salesSlabs: [],
    status: 'Active',
    isDefault: false
  });

  useEffect(() => {
    fetchCommissionPlans();
  }, []);

const fetchCommissionPlans = async () => {
  try {
    setLoading(true);
    const response = await AdminAffiliateApiService.getCommissionPlans();

    const list = response?.data?.plans || [];
    setPlans(Array.isArray(list) ? list : []);
  } catch (error) {
    console.error("Error fetching commission plans:", error);
    setPlans([]); // safety
  } finally {
    setLoading(false);
  }
};

const handleCreatePlan = () => {
  setEditingPlan(null);
  setFormData({
    name: '',
    description: '',
    commissionRate: '',
    bonusStructure: '',
    salesSlabs: [],
    status: 'Active',
    isDefault: false
  });
  setShowModal(true);
};

const handleEditPlan = (plan) => {
  setEditingPlan(plan);
  setFormData({
    name: plan.name || '',
    description: plan.description || '',
    commissionRate: plan.commissionRate || '',
    bonusStructure: plan.bonusStructure || '',
    salesSlabs: plan.salesSlabs || [],
    status: plan.status || 'Active',
    isDefault: plan.isDefault || false
  });
  setShowModal(true);
};

const handleSavePlan = async () => {
  try {
    if (editingPlan) {
      await AdminAffiliateApiService.updateCommissionPlan(editingPlan._id, formData);
    } else {
      await AdminAffiliateApiService.createCommissionPlan(formData);
    }
    setShowModal(false);
    fetchCommissionPlans();
  } catch (error) {
    console.error("Error saving commission plan:", error);
  }
};

const handleDeletePlan = async (planId) => {
  if (window.confirm('Are you sure you want to delete this commission plan?')) {
    try {
      await AdminAffiliateApiService.deleteCommissionPlan(planId);
      fetchCommissionPlans();
    } catch (error) {
      console.error("Error deleting commission plan:", error);
    }
  }
};

const handleStatusChange = async (planId, newStatus) => {
  try {
    await AdminAffiliateApiService.updateCommissionPlanStatus(planId, newStatus);
    fetchCommissionPlans();
  } catch (error) {
    console.error("Error updating commission plan status:", error);
  }
};

const handleInputChange = (e) => {
  const { name, value, type, checked } = e.target;
  setFormData(prev => ({
    ...prev,
    [name]: type === 'checkbox' ? checked : value
  }));
};

const handleSalesSlabChange = (index, field, value) => {
  const newSalesSlabs = [...formData.salesSlabs];
  newSalesSlabs[index] = {
    ...newSalesSlabs[index],
    [field]: value
  };
  setFormData(prev => ({
    ...prev,
    salesSlabs: newSalesSlabs
  }));
};

const addSalesSlab = () => {
  setFormData(prev => ({
    ...prev,
    salesSlabs: [...prev.salesSlabs, { min: 0, max: null, rate: '' }]
  }));
};

const removeSalesSlab = (index) => {
  const newSalesSlabs = formData.salesSlabs.filter((_, i) => i !== index);
  setFormData(prev => ({
    ...prev,
    salesSlabs: newSalesSlabs
  }));
};

const filteredPlans = (Array.isArray(plans) ? plans : []).filter((plan) => {
  const name = String(plan?.name || "").toLowerCase();
  const desc = String(plan?.description || "").toLowerCase();
  const q = String(searchTerm || "").toLowerCase();
  return name.includes(q) || desc.includes(q);
});

  return (
    <div className="affiliate-cp-page">
      <div className="admin-page-header">
        <h1>Commission Plans</h1>
        <p>Create and manage multiple commission plans. Assign to affiliates or sellers.</p>
      </div>

      <div className="affiliate-cp-actions-bar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search plans..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="affiliate-cp-action-buttons">
          <button className="affiliate-cp-btn-primary" onClick={handleCreatePlan}>
            <FaPlus /> New Plan
          </button>
          <button className="commission-plan-btn-secondary">
            <FaFilter /> Filter
          </button>
        </div>
      </div>

      <div className="affiliate-cp-plans-grid">
        {loading ? (
          <div className="affiliate-cp-loading-state">Loading commission plans...</div>
        ) : filteredPlans.length === 0 ? (
          <div className="empty-state">No commission plans found</div>
        ) : (
         filteredPlans.map((plan) => (
  <div key={plan?._id || plan?.id} className="affiliate-cp-plan-card">
              <div className="plan-header">
                <div className="affiliate-cp-plan-info">
                  <h3>{plan.name}</h3>
                  <p>{plan.description}</p>
                </div>
                <div className="affiliate-cp-plan-actions">
                  <button className="commission-plan-btn-icon commission-plan-btn-copy">
                    <FaCopy />
                  </button>
                  <button className="affiliate-cp-btn-icon affiliate-cp-btn-edit" onClick={() => handleEditPlan(plan)}>
                    <FaEdit />
                  </button>
                  <button className="affiliate-cp-btn-icon affiliate-cp-btn-delete" onClick={() => handleDeletePlan(plan._id)}>
                    <FaTrash />
                  </button>
                </div>
              </div>
              
              <div className="affiliate-cp-plan-details">
                <div className="affiliate-cp-plan-stats">
                  <div className="stat-item">
                    <span className="stat-label">Commission Rate</span>
                    <span className="cp-stat-value">{plan.commissionRate}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Type</span>
                    <span className="cp-stat-value">{plan.type}</span>
                  </div>
                  <div className="stat-item">
                    <span className="affiliate-cp-stat-label">Status</span>
                   <span className={`affiliate-cp-status-badge ${(plan?.status || "Active").toLowerCase()}`}>
      {plan?.status || "Active"}
    </span>
                  </div>
                  <div className="stat-item">
                    <span className="affiliate-cp-stat-label">Default</span>
                    <span className={`default-badge ${plan?.isDefault ? 'default-yes' : 'default-no'}`}>
                      {plan?.isDefault ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>

                <div className="affiliate-cp-plan-bonus">
                  <h4>Bonus Structure</h4>
                  <p>{plan.bonusStructure}</p>
                </div>

                <div className="affiliate-cp-plan-slabs">
                  <h4>Sales Slabs</h4>
                  <div className="affiliate-cp-slabs-list">
      {(Array.isArray(plan?.salesSlabs) ? plan.salesSlabs : []).map((slab, index) => (
        <div key={index} className="slab-item">
          <span className="affiliate-cp-slab-range">
            {slab?.min ?? 0} - {slab?.max ?? "∞"} sales
          </span>
          <span className="affiliate-cp-slab-rate">{slab?.rate ?? "-"}</span>
        </div>
      ))}
    </div>
                </div>

                <div className="affiliate-cp-plan-metrics">
                  <div className="metric">
                    <span className="affiliate-cp-metric-label">Affiliates</span>
                    <span className="affiliate-cp-metric-value">{plan.affiliatesCount}</span>
                  </div>
                  <div className="metric">
                    <span className="affiliate-cp-metric-label">Sellers</span>
                    <span className="affiliate-cp-metric-value">{plan.sellersCount}</span>
                  </div>
                  <div className="metric">
                    <span className="affiliate-cp-metric-label">Total Payout</span>
                    <span className="affiliate-cp-metric-value">₹{Number(plan?.totalPayout || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal for Create/Edit Commission Plan */}
      {showModal && (
        <div className="affiliate-cp-modal-overlay">
          <div className="affiliate-cp-modal-content">
            <div className="affiliate-cp-modal-header">
              <h2>{editingPlan ? 'Edit Commission Plan' : 'Create Commission Plan'}</h2>
              <button className="affiliate-cp-modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className="affiliate-cp-modal-body">
              <div className="affiliate-cp-form-group">
                <label>Plan Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter plan name"
                  required
                />
              </div>
              <div className="affiliate-cp-form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter plan description"
                  rows="3"
                  required
                />
              </div>
              <div className="affiliate-cp-form-group">
                <label>Commission Rate (%)</label>
                <input
                  type="number"
                  name="commissionRate"
                  value={formData.commissionRate}
                  onChange={handleInputChange}
                  placeholder="Enter commission rate"
                  min="0"
                  max="100"
                  step="0.1"
                  required
                />
              </div>
              <div className="affiliate-cp-form-group">
                <label>Bonus Structure</label>
                <textarea
                  name="bonusStructure"
                  value={formData.bonusStructure}
                  onChange={handleInputChange}
                  placeholder="Enter bonus structure details"
                  rows="2"
                />
              </div>
              <div className="affiliate-cp-form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>
              <div className="affiliate-cp-form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleInputChange}
                  />
                  Set as default commission plan
                </label>
              </div>
              <div className="affiliate-cp-form-group">
                <label>Sales Slabs</label>
                {formData.salesSlabs.map((slab, index) => (
                  <div key={index} className="affiliate-cp-sales-slab-item">
                    <div className="affiliate-cp-slab-row">
                      <input
                        type="number"
                        placeholder="Min"
                        value={slab.min}
                        onChange={(e) => handleSalesSlabChange(index, 'min', e.target.value)}
                        min="0"
                      />
                      <input
                        type="number"
                        placeholder="Max (leave empty for infinity)"
                        value={slab.max || ''}
                        onChange={(e) => handleSalesSlabChange(index, 'max', e.target.value)}
                        min="0"
                      />
                      <input
                        type="text"
                        placeholder="Rate"
                        value={slab.rate}
                        onChange={(e) => handleSalesSlabChange(index, 'rate', e.target.value)}
                      />
                      <button
                        type="button"
                        className="affiliate-cp-btn-remove"
                        onClick={() => removeSalesSlab(index)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-add"
                  onClick={addSalesSlab}
                >
                  Add Sales Slab
                </button>
              </div>
            </div>
            <div className="affiliate-cp-modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className="affiliate-cp-btn-save" onClick={handleSavePlan}>
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
